import { AITool } from '../types'

export const addToFavoritesTool: AITool = {
  name: 'add_to_favorites',
  description: 'Add one or more cryptocurrency symbols to the favorites watchlist with visual demonstration',
  category: 'watchlist',
  parameters: [
    {
      name: 'symbols',
      type: 'array',
      description: 'Array of trading pair symbols to add to favorites (e.g., ["BTCUSDT", "ETHUSDT"])',
      required: true,
      items: {
        type: 'string',
        description: 'Trading pair symbol (e.g., "BTCUSDT")'
      }
    }
  ],
  examples: [
    'Add BTCUSDT to my favorites',
    'Add BTC, ETH and ADA to my watchlist',
    'Add these symbols to favorites: SOLUSDT, DOTUSDT, LINKUSDT'
  ],
  execute: async (params, context) => {
    console.log('⭐ Watchlist: Adding symbols to favorites:', params)
    
    // Trigger visual favorites addition through a global event
    const favoritesEvent = new CustomEvent('addToFavorites', {
      detail: params
    })
    
    // Set up a promise to listen for the result
    const resultPromise = new Promise((resolve) => {
      const handleResult = (event: CustomEvent) => {
        console.log('📊 Received favorites result:', event.detail)
        window.removeEventListener('addToFavoritesResult', handleResult as EventListener)
        resolve(event.detail)
      }
      window.addEventListener('addToFavoritesResult', handleResult as EventListener)
      
      // Timeout after 15 seconds
      setTimeout(() => {
        window.removeEventListener('addToFavoritesResult', handleResult as EventListener)
        resolve({
          success: false,
          error: 'Add to favorites timeout - Watchlist component not responding',
          ...params,
          timestamp: new Date()
        })
      }, 15000)
    })
    
    // Dispatch the event
    window.dispatchEvent(favoritesEvent)
    
    // Wait for the result
    const result = await resultPromise
    
    return {
      success: true,
      result,
      message: `Added symbols to favorites: ${params.symbols.join(', ')}`
    }
  }
}

export const getFavoritesDataTool: AITool = {
  name: 'get_favorites_data',
  description: 'Get current market data for all symbols in the favorites watchlist',
  category: 'watchlist',
  parameters: [
    {
      name: 'sortBy',
      type: 'string',
      description: 'Sort criteria: symbol, price, priceChangePercent, volume',
      required: false,
      default: 'priceChangePercent',
      enum: ['symbol', 'price', 'priceChangePercent', 'volume']
    },
    {
      name: 'sortDirection',
      type: 'string',
      description: 'Sort direction: asc or desc',
      required: false,
      default: 'desc',
      enum: ['asc', 'desc']
    }
  ],
  examples: [
    'Show my favorite symbols',
    'What are my top performing favorites?',
    'Get my watchlist sorted by volume',
    'Show favorites sorted by price change'
  ],
  execute: async (params, context) => {
    console.log('📊 Watchlist: Getting favorites data:', params)
    
    // Trigger get favorites data through a global event
    const getFavoritesEvent = new CustomEvent('getFavoritesData', {
      detail: params
    })
    
    // Set up a promise to listen for the result
    const resultPromise = new Promise((resolve) => {
      const handleResult = (event: CustomEvent) => {
        console.log('📈 Received favorites data result:', event.detail)
        window.removeEventListener('getFavoritesDataResult', handleResult as EventListener)
        resolve(event.detail)
      }
      window.addEventListener('getFavoritesDataResult', handleResult as EventListener)
      
      // Timeout after 10 seconds
      setTimeout(() => {
        window.removeEventListener('getFavoritesDataResult', handleResult as EventListener)
        resolve({
          success: false,
          error: 'Get favorites data timeout - Watchlist component not responding',
          ...params,
          timestamp: new Date()
        })
      }, 10000)
    })
    
    // Dispatch the event
    window.dispatchEvent(getFavoritesEvent)
    
    // Wait for the result
    const result = await resultPromise
    
    return {
      success: true,
      result,
      message: 'Retrieved favorites data successfully'
    }
  }
}

export const getTopMoversFromFavoritesTool: AITool = {
  name: 'get_top_movers_favorites',
  description: 'Get the top gaining or losing symbols from the favorites watchlist',
  category: 'watchlist',
  parameters: [
    {
      name: 'type',
      type: 'string',
      description: 'Type of movers to get: gainers or losers',
      required: false,
      default: 'gainers',
      enum: ['gainers', 'losers']
    },
    {
      name: 'limit',
      type: 'number',
      description: 'Number of top movers to return',
      required: false,
      default: 5
    }
  ],
  examples: [
    'Show my top gaining favorites',
    'What are my worst performing favorites today?',
    'Top 3 gainers from my watchlist',
    'Show the biggest losers in my favorites'
  ],
  execute: async (params, context) => {
    console.log('🚀 Watchlist: Getting top movers from favorites:', params)
    
    // Trigger get top movers through a global event
    const getTopMoversEvent = new CustomEvent('getTopMoversFromFavorites', {
      detail: params
    })
    
    // Set up a promise to listen for the result
    const resultPromise = new Promise((resolve) => {
      const handleResult = (event: CustomEvent) => {
        console.log('📊 Received top movers result:', event.detail)
        window.removeEventListener('getTopMoversFromFavoritesResult', handleResult as EventListener)
        resolve(event.detail)
      }
      window.addEventListener('getTopMoversFromFavoritesResult', handleResult as EventListener)
      
      // Timeout after 10 seconds
      setTimeout(() => {
        window.removeEventListener('getTopMoversFromFavoritesResult', handleResult as EventListener)
        resolve({
          success: false,
          error: 'Get top movers timeout - Watchlist component not responding',
          ...params,
          timestamp: new Date()
        })
      }, 10000)
    })
    
    // Dispatch the event
    window.dispatchEvent(getTopMoversEvent)
    
    // Wait for the result
    const result = await resultPromise
    
    return {
      success: true,
      result,
      message: `Retrieved top ${params.type || 'gainers'} from favorites`
    }
  }
}

export const bulkTradeFromFavoritesTool: AITool = {
  name: 'bulk_trade_favorites',
  description: 'Place trading orders for multiple symbols from the favorites watchlist',
  category: 'watchlist',
  parameters: [
    {
      name: 'side',
      type: 'string',
      description: 'Order side: BUY or SELL',
      required: true,
      enum: ['BUY', 'SELL']
    },
    {
      name: 'quantityPerSymbol',
      type: 'number',
      description: 'Amount to trade for each symbol',
      required: true
    },
    {
      name: 'type',
      type: 'string',
      description: 'Order type: MARKET, LIMIT, or STOP',
      required: false,
      default: 'MARKET',
      enum: ['MARKET', 'LIMIT', 'STOP']
    },
    {
      name: 'symbols',
      type: 'array',
      description: 'Specific symbols to trade (if not provided, uses all favorites)',
      required: false,
      items: {
        type: 'string',
        description: 'Trading pair symbol (e.g., "BTCUSDT")'
      }
    },
    {
      name: 'filterBy',
      type: 'string',
      description: 'Filter favorites by criteria before trading: gainers, losers, all',
      required: false,
      default: 'all',
      enum: ['gainers', 'losers', 'all']
    },
    {
      name: 'limit',
      type: 'number',
      description: 'Maximum number of symbols to trade (when using filterBy)',
      required: false,
      default: 10
    }
  ],
  examples: [
    'Buy 100 USDT worth of each symbol in my favorites',
    'Sell 0.1 of each gainer in my watchlist',
    'Buy market orders for top 5 gainers from favorites',
    'Sell all my favorite symbols with 50 USDT each'
  ],
  execute: async (params, context) => {
    console.log('💰 Watchlist: Bulk trading from favorites:', params)
    
    // Trigger bulk trade through a global event
    const bulkTradeEvent = new CustomEvent('bulkTradeFromFavorites', {
      detail: params
    })
    
    // Set up a promise to listen for the result
    const resultPromise = new Promise((resolve) => {
      const handleResult = (event: CustomEvent) => {
        console.log('📈 Received bulk trade result:', event.detail)
        window.removeEventListener('bulkTradeFromFavoritesResult', handleResult as EventListener)
        resolve(event.detail)
      }
      window.addEventListener('bulkTradeFromFavoritesResult', handleResult as EventListener)
      
      // Timeout after 60 seconds for bulk trading
      setTimeout(() => {
        window.removeEventListener('bulkTradeFromFavoritesResult', handleResult as EventListener)
        resolve({
          success: false,
          error: 'Bulk trade timeout - operation took too long',
          ...params,
          timestamp: new Date()
        })
      }, 60000)
    })
    
    // Dispatch the event
    window.dispatchEvent(bulkTradeEvent)
    
    // Wait for the result
    const result = await resultPromise
    
    return {
      success: true,
      result,
      message: `Bulk trade completed for ${params.side} orders`
    }
  }
}

export const searchAndAddSymbolTool: AITool = {
  name: 'search_and_add_symbol',
  description: 'Search for a symbol in the watchlist and add it to favorites with visual demonstration',
  category: 'watchlist',
  parameters: [
    {
      name: 'searchTerm',
      type: 'string',
      description: 'Symbol or partial symbol to search for (e.g., BTC, ETH, SOL)',
      required: true
    },
    {
      name: 'addToFavorites',
      type: 'boolean',
      description: 'Whether to add the found symbol to favorites',
      required: false,
      default: true
    }
  ],
  examples: [
    'Search for BTC and add to favorites',
    'Find SOLANA and add it to my watchlist',
    'Search for DOT in the market'
  ],
  execute: async (params, context) => {
    console.log('🔍 Watchlist: Searching and adding symbol:', params)
    
    // Trigger search and add through a global event
    const searchAddEvent = new CustomEvent('searchAndAddSymbol', {
      detail: params
    })
    
    // Set up a promise to listen for the result
    const resultPromise = new Promise((resolve) => {
      const handleResult = (event: CustomEvent) => {
        console.log('🔍 Received search and add result:', event.detail)
        window.removeEventListener('searchAndAddSymbolResult', handleResult as EventListener)
        resolve(event.detail)
      }
      window.addEventListener('searchAndAddSymbolResult', handleResult as EventListener)
      
      // Timeout after 20 seconds
      setTimeout(() => {
        window.removeEventListener('searchAndAddSymbolResult', handleResult as EventListener)
        resolve({
          success: false,
          error: 'Search and add timeout - Watchlist component not responding',
          ...params,
          timestamp: new Date()
        })
      }, 20000)
    })
    
    // Dispatch the event
    window.dispatchEvent(searchAddEvent)
    
    // Wait for the result
    const result = await resultPromise
    
    return {
      success: true,
      result,
      message: `Search and add operation completed for: ${params.searchTerm}`
    }
  }
}

export const watchlistTools: AITool[] = [
  addToFavoritesTool,
  getFavoritesDataTool,
  getTopMoversFromFavoritesTool,
  bulkTradeFromFavoritesTool,
  searchAndAddSymbolTool
] 